import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { map, catchError, tap, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

interface KeycloakTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_expires_in: number;
  token_type: string;
  scope: string;
}

@Injectable({
  providedIn: 'root'
})
export class RuleEngineAuthService {
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private tokenExpiryTime: number = 0;
  private tokenRequest$: Observable<string> | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Ottiene il token Keycloak (cached o nuovo)
   */
  getToken(): Observable<string> {
    // Se il token è ancora valido, restituiscilo
    if (this.tokenSubject.value && this.isTokenValid()) {
      return new Observable<string>((observer) => {
        observer.next(this.tokenSubject.value!);
        observer.complete();
      });
    }

    // Se c'è già una richiesta in corso, condividila
    if (this.tokenRequest$) {
      return this.tokenRequest$;
    }

    // Altrimenti, richiedi un nuovo token
    this.tokenRequest$ = this.requestNewToken().pipe(
      tap(() => {
        this.tokenRequest$ = null; // Reset dopo completamento
      }),
      shareReplay(1) // Condividi il risultato con subscriber multipli
    );

    return this.tokenRequest$;
  }

  /**
   * Richiede un nuovo token a Keycloak usando client_credentials
   */
  private requestNewToken(): Observable<string> {
    const config = environment.ruleEngine.keycloak;
    
    const body = new URLSearchParams();
    body.set('grant_type', 'client_credentials');
    body.set('client_id', config.clientId);
    body.set('client_secret', config.clientSecret);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<KeycloakTokenResponse>(config.tokenEndpoint, body.toString(), { headers }).pipe(
      map((response) => {
        const token = response.access_token;
        const expiresIn = response.expires_in;

        // Salva il token e calcola quando scade (con 30 secondi di margine)
        this.tokenSubject.next(token);
        this.tokenExpiryTime = Date.now() + (expiresIn - 30) * 1000;

        console.log('✅ RuleEngine token obtained, expires in', expiresIn, 'seconds');
        return token;
      }),
      catchError((error) => {
        console.error('❌ Failed to obtain RuleEngine token:', error);
        this.tokenSubject.next(null);
        return throwError(() => error);
      })
    );
  }

  /**
   * Verifica se il token corrente è ancora valido
   */
  private isTokenValid(): boolean {
    return Date.now() < this.tokenExpiryTime;
  }

  /**
   * Invalida il token corrente (forza refresh)
   */
  invalidateToken(): void {
    this.tokenSubject.next(null);
    this.tokenExpiryTime = 0;
  }
}
